"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Columns3, Compass, Flame, Hammer, Layers, Maximize2, Paintbrush, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface ModernizationFacet {
  id: string;
  title: string;
  category: string;
  iconName: string;
  description: string;
  specs: string[];
  techNote: string;
}
const facets: ModernizationFacet[] = [{
  id: "structural-open-concept",
  title: "Open-Concept Engineering & Load-Bearing Removal",
  category: "Structural Architecture",
  iconName: "Columns3",
  description: "Transform compartmentalized layouts into continuous living environments with precision-calculated steel beams and recessed flush framing.",
  specs: ["Engineered W-shape structural steel and LVL beam installation", "City of Brookhaven structural permitting and architectural seal", "Temporary hydraulic shoring walls and zero-deflection tolerances"],
  techNote: "Includes complete mechanical, electrical, and plumbing re-routing through structural soffits."
}, {
  id: "hardwood-refinishing",
  title: "Hardwood Flooring & Custom Refinishing",
  category: "Flooring & Millwork",
  iconName: "Layers",
  description: "Install architectural wide-plank white oak or restore historic Atlanta tongue-and-groove boards with durable, low-emission finishes.",
  specs: ["Up to 9-inch wide European white oak live-sawn planks", "Multi-stage dustless sanding with precision moisture testing", "Natural Swedish matte sealants and zero-VOC cure formulations"],
  techNote: "Seamless flush transitions crafted at all stone, tile, and exterior thresholds."
}, {
  id: "finish-carpentry",
  title: "Architectural Trim & Finish Carpentry",
  category: "Detail & Millwork",
  iconName: "Hammer",
  description: "Define volume and balance through custom-proportioned interior mouldings, authentic coffered ceilings, and tailored wall paneling.",
  specs: ["Custom two-piece and three-piece crown profiles with backer reveals", "true craftsman beam coffering and tongue-and-groove ceiling wraps", "Substantial 7-inch to 9-inch square-edge baseboards and plinths"],
  techNote: "Built with furniture-grade select hardwoods and polyurethane core composite backings."
}, {
  id: "artisanal-fireplaces",
  title: "Artisanal Fireplace Surrounds",
  category: "Masonry & Stone",
  iconName: "Flame",
  description: "Anchor formal living and family gathering areas with floor-to-ceiling honed quartzite, bookmatched marble, or solid timber mantels.",
  specs: ["Floor-to-ceiling slab fabrication with continuous vein matching", "Handcrafted solid rift-cut white oak floating mantels", "Integrated linear gas convertibility and low-profile recessed media"],
  techNote: "Non-combustible sub-framing engineered to meet strict Brookhaven fire setback codes."
}, {
  id: "cohesive-color",
  title: "Cohesive Paint & Curated Color Palettes",
  category: "Finishes & Ambiance",
  iconName: "Paintbrush",
  description: "Coordinate warm architectural palettes and artisanal finishes that maximize natural light throughout the entire living level.",
  specs: ["In-home natural light analysis and multi-angle sample boards", "Breathable mineral limewash and Roman clay accent wall textures", "Scuff-resistant low-sheen eggshell enamels on high-traffic corridors"],
  techNote: "Precision spray lacquering for all built-in casework and architectural passage doors."
}];
interface ShowcaseItem {
  id: string;
  tag: string;
  title: string;
  location: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
}
const showcases: ShowcaseItem[] = [{
  id: "showcase-1",
  tag: "Spatial Flow",
  title: "Ashford Park Whole-Floor Modernization",
  location: "Brookhaven, GA",
  description: "Removed three structural partition walls to create a continuous 38-foot light axis spanning from kitchen through living lounge.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_living_transformation.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_living_transformation.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_interior_living_transformation.webp-wvc-sizes"
}, {
  id: "showcase-2",
  tag: "Fine Joinery",
  title: "Historic Brookhaven Ceiling & Hearth Suite",
  location: "Brookhaven, GA",
  description: "Restored historic oak subfloors, installed coffered ceiling geometry, and sculpted a honed travertine fireplace chimney breast.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_craftsman_trim_fireplace.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_craftsman_trim_fireplace.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_craftsman_trim_fireplace.png-wvc-sizes"
}];
function renderFacetIcon(name: string) {
  const iconProps = {
    className: "size-5 text-primary stroke-[1.75]"
  };
  switch (name) {
    case "Columns3":
      return <Columns3 {...iconProps} />;
    case "Layers":
      return <Layers {...iconProps} />;
    case "Hammer":
      return <Hammer {...iconProps} />;
    case "Flame":
      return <Flame {...iconProps} />;
    case "Paintbrush":
      return <Paintbrush {...iconProps} />;
    default:
      return <Sparkles {...iconProps} />;
  }
}
export default function Modernization() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 15
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="interior-remodeling" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      {/* Subtle architectural background texture */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[radial-gradient(#111827_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-[1360px] relative z-10">
        {/* Header Block */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-3xl mb-16 md:mb-20">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-px w-8 bg-primary/60" />
            <span className="text-xs font-semibold tracking-[0.14em] uppercase text-muted-foreground font-sans">
              Craftsmanship & Spatial Design
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight text-foreground leading-[1.18] mb-6">
            Bespoke interior transformations tailored to Brookhaven homes.
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
            We open congested floor plans, install structural steel beams, and hand-craft custom millwork so your entire main level flows with natural light and timeless architectural poise.
          </p>
        </motion.div>

        {/* 5 Core Modernization Facets Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
          {facets.map((facet, i) => <motion.div key={facet.id} data-index={i} variants={itemVariants} className={i === 0 ? "lg:col-span-2" : "col-span-1"}>
              <Card className="h-full rounded-2xl border border-border/80 bg-card/85 backdrop-blur-md shadow-sm transition-[transform,box-shadow,border-color] duration-250 hover:-translate-y-1 hover:border-primary/40 hover:shadow-md flex flex-col justify-between overflow-hidden">
                <CardContent className="p-7 sm:p-8 flex flex-col h-full justify-between gap-6">
                  <div>
                    {/* Header Bar within Card */}
                    <div className="flex items-center justify-between gap-4 mb-5">
                      <div className="size-11 rounded-xl bg-accent flex items-center justify-center border border-border/60">
                        {renderFacetIcon(facet.iconName)}
                      </div>
                      <Badge variant="outline" className="rounded-full px-3 py-1 text-[11px] font-medium tracking-wider uppercase bg-muted/60 border-border text-foreground">
                        {facet.category}
                      </Badge>
                    </div>

                    <h3 className="text-xl sm:text-2xl font-serif font-medium text-foreground tracking-tight mb-3">
                      {facet.title}
                    </h3>

                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6 font-sans">
                      {facet.description}
                    </p>

                    <Separator className="mb-6 bg-border/70" />

                    {/* Specifications List */}
                    <div className="space-y-2.5 mb-6">
                      <p className="text-xs font-semibold tracking-wider uppercase text-foreground/80 font-sans">
                        Architectural Deliverables
                      </p>
                      <ul className="space-y-2">
                        {facet.specs.map((spec, specIdx) => <li key={specIdx} data-index={specIdx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-sans leading-normal">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{spec}</span>
                          </li>)}
                      </ul>
                    </div>
                  </div>

                  {/* Technical Footnote Note Box */}
                  <div className="rounded-xl bg-muted/60 border border-border/80 p-3.5 mt-auto">
                    <div className="flex items-center gap-2 text-xs font-semibold text-foreground mb-1">
                      <Compass className="size-3.5 text-primary" />
                      <span>Technical Protocol</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-sans leading-relaxed">
                      {facet.techNote}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Architectural Showcase: Before & After Spatial Flow */}
        <div className="mb-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <span className="text-xs font-semibold tracking-[0.14em] uppercase text-muted-foreground font-sans">
                Realized Projects
              </span>
              <h3 className="text-2xl sm:text-3xl font-serif font-semibold text-foreground tracking-tight mt-1">
                Visualizing spatial flow and refined finish carpentry
              </h3>
            </div>
            <Link to="/OurProjectsPage" className="inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors group">
              <span>Explore residential project gallery</span>
              <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {showcases.map((project, i) => <motion.div key={project.id} data-index={i} initial={{
            opacity: shouldReduceMotion ? 1 : 0,
            y: shouldReduceMotion ? 0 : 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className="group rounded-2xl overflow-hidden bg-card border border-border/80 shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 flex flex-col">
                <div className="relative overflow-hidden bg-muted">
                  <AspectRatio ratio={16 / 10}>
                    <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </AspectRatio>
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center rounded-full bg-secondary/90 text-secondary-foreground text-xs font-medium px-3 py-1 backdrop-blur-sm shadow-xs">
                      {project.tag}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center rounded-full bg-background/90 text-foreground text-xs font-medium px-3 py-1 backdrop-blur-sm border border-border/60">
                      {project.location}
                    </span>
                  </div>
                </div>

                <div className="p-6 sm:p-7 flex flex-col justify-between grow">
                  <div>
                    <h4 className="text-xl font-serif font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {project.title}
                    </h4>
                    <p className="text-sm text-muted-foreground font-sans leading-relaxed">
                      {project.description}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-border flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">
                      Brookhaven Elevation Renovations
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                      <Maximize2 className="size-3.5" />
                      Architectural Case Study
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Consultation Callout Panel */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="rounded-3xl bg-secondary text-secondary-foreground p-8 sm:p-12 lg:p-14 relative overflow-hidden border border-border/40 shadow-xl">
          {/* Subtle architectural gold hairline framing */}
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />

          <div className="relative z-10 max-w-3xl">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-semibold tracking-tight text-secondary-foreground mb-4">
              Planning an interior modernization for your Brookhaven residence?
            </h3>

            <p className="text-sm sm:text-base text-secondary-foreground/80 font-sans leading-relaxed mb-8 max-w-2xl">
              Meet our principal builder and lead designer at your home. We evaluate structural walls, draft preliminary span concepts, and provide transparent cost guidance before you commit.
            </p>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold px-8 py-3.5 hover:bg-primary/90 transition-[color,background-color,box-shadow] shadow-md">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an in-home consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-secondary-foreground bg-transparent hover:bg-secondary-foreground/10 transition-[color,background-color,border-color]">
                <Link to="/AboutPage">Review our 4-step design-build process</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}